import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { CashJournalModal } from './CashJournalModal.jsx';
import { CashEntryModal } from './CashEntryModal.jsx';

export function CashJournalPage() {
  const { t } = useTranslation();
  const [journalId, setJournalId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);

  const journals = useQuery({ queryKey: ['finance', 'cash-journals'], queryFn: () => financeApi.listCashJournals().then((r) => r.items) });
  useEffect(() => {
    if (!journalId && (journals.data || []).length) setJournalId(journals.data[0].id);
  }, [journals.data, journalId]);

  const detail = useQuery({ queryKey: ['finance', 'cash-journal', journalId], queryFn: () => financeApi.getCashJournal(journalId).then((r) => r.items), enabled: Boolean(journalId) });
  const journal = detail.data;
  const entries = journal?.entries || [];

  const columns = [
    { key: 'number', label: t('cb.col.number'), render: (r) => <span className="mono">{r.entryNumber}</span> },
    { key: 'date', label: t('cb.col.date'), render: (r) => formatDate(r.entryDate) },
    { key: 'type', label: t('cb.col.type'), render: (r) => <Badge variant={r.type === 'RECEIPT' ? 'success' : 'warning'}>{t(`cb.type.${r.type}`)}</Badge> },
    { key: 'desc', label: t('cb.col.description'), render: (r) => <span>{r.description}{r.category ? <span className="muted text-sm"> · {r.category}</span> : null}</span> },
    { key: 'amount', label: t('cb.col.amount'), align: 'right', render: (r) => <span className="mono num" style={{ color: r.type === 'RECEIPT' ? 'var(--color-success, #059669)' : 'var(--color-danger, #dc2626)' }}>{r.type === 'PAYMENT' ? '−' : '+'}{formatCurrency(r.amount)}</span> },
    { key: 'balance', label: t('cb.col.balance'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.balanceAfter)}</span> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cb.title')}</h1>
          <p className="muted">{t('cb.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 220 }} value={journalId} onChange={(e) => setJournalId(e.target.value)}>
            {(journals.data || []).map((j) => <option key={j.id} value={j.id}>{j.code} · {j.name}</option>)}
          </select>
          <Button variant="ghost" onClick={() => setCreateOpen(true)}>{t('cb.action.newJournal')}</Button>
          <Button variant="primary" disabled={!journalId} onClick={() => setEntryOpen(true)}>{t('cb.action.post')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cb.metric.balance')} value={formatCurrency(journal?.balance || 0)} trendVariant={Number(journal?.balance) > 0 ? 'success' : 'neutral'} />
        <MetricCard label={t('cb.metric.entries')} value={String(entries.length)} />
        <MetricCard label={t('cb.metric.currency')} value={journal?.currency || '-'} />
      </div>

      <Table columns={columns} rows={entries} isLoading={detail.isLoading} emptyMessage={t('cb.empty')} />

      <CashJournalModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => setJournalId(id)} />
      <CashEntryModal open={entryOpen} onClose={() => setEntryOpen(false)} journalId={journalId} />
    </section>
  );
}
